/**
 * CompanyScopeContext.jsx — 회사 선택창 상태 (2026-09-03 회사 선택창 개편 1차)
 *
 * 사이드바 상단 선택창의 진실원본. 권한 따라 자동 축소(사용자 확정):
 *   - 운영자(role=admin): 테넌트 → 회사 2단 선택 (saas 장부 API — 기성품 재사용, 서버 무변경)
 *   - 비운영자(테넌트 관리자·배정 관리자): 자기 회사 + /api/my/external-companies (회사만 선택)
 *   - 일반 직원(접근 회사 0): 선택창 자체가 없음 → 현행 사이드바 그대로
 *
 * company.kind:
 *   'primary' — 운영자가 본 플랫폼 테넌트의 대표 회사 → 일반 메뉴 그룹 전체 (현행 파인 메뉴)
 *   'own'     — 비운영자의 자기 회사 → 일반 그룹 + 자기 회사 ext 잔여(구 아코디언 평평 모드)
 *   'ext'     — 그 외 회사 → companyMenuSet 16종 + 전역 그룹
 *
 * 통합관리(2026-10-01, 회사 칸 맨 아래): 회사가 아니다 — 지금 주소가 통합 주소면 통합 모드(integratedScope.js).
 *   고르면 통합 관리 홈으로 갈 뿐 회사값·저장소·서버 회사값(X-Scope-Company)은 건드리지 않는다.
 * 2026-10-02 분리(292줄): 통합관리 판단 → useIntegratedAccess.js, 고객 테넌트 보기 → useCustomerTenantView.js (동작 변화 없음).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../api/apiClient';
import { writeScopeCompany } from '../../../api/scopeCompany';
import { isAdminUser } from './sidebarFilters';
import { resolveHqCompanyCode } from './companyFeatures';
import { MENU_GROUPS, ADMIN_ONLY_MENUS } from './menuConfig';
import { GLOBAL_GROUP_NAMES } from './companyMenuSet';
import { buildStayPaths, resolveCompanySwitchTarget, backToCompanyScreen, useSwitchGuard } from './companySwitchTarget';
import { lastCompanyScreen, skipIntegratedRestoreOnce } from '../../../utils/lastScreen';
import { useTenantMenuInfo } from './tenantMenuInfo';
import { isCallAllVisible } from './callMenuNaming';
import { INTEGRATED_OPTION, isIntegratedPath, exitIntegratedTarget } from './integratedScope';
import { useIntegratedAccess } from './useIntegratedAccess';
import { useCustomerTenantView } from './useCustomerTenantView';

const LS_TENANT = 'companyScope.tenant';
const LS_COMPANY = 'companyScope.company';
const CompanyScopeContext = createContext(null);
const NO_INTEGRATED = { list: [], loaded: true, refresh: () => {} };

export function useCompanyScope() {
  return useContext(CompanyScopeContext);
}

export function CompanyScopeProvider({
  me, externalCompanies = [], integratedList = NO_INTEGRATED, homeKey = false, boardKey = false, callHqKey = false, pathname = '', children,
  menuManageKey = null, // '통합 메뉴 관리' 열쇠(2026-10-15): true/false, 열쇠 목록을 아직 모르면 null
}) {
  const isOperator = isAdminUser();
  const navigate = useNavigate();
  const myCompanyCode = String(me?.company?.code || me?.company_code || '').toUpperCase();

  const [tenants, setTenants] = useState([]);                 // 운영자 전용
  const [tenantCode, setTenantCode] = useState(() => localStorage.getItem(LS_TENANT) || '');
  const [ledgerCompanies, setLedgerCompanies] = useState([]); // 운영자: 선택 테넌트의 회사들
  const [ledgerTenant, setLedgerTenant] = useState('');       // 그 목록이 어느 테넌트 것인지
  const [companyCode, setCompanyCode] = useState(() => localStorage.getItem(LS_COMPANY) || '');

  // ── 운영자: 테넌트 목록 (활성만) ──────────────────────────────────────────
  useEffect(() => {
    if (!isOperator) return;
    apiClient.get('/api/admin/saas/tenants').then((r) => {
      if (!r.ok || !Array.isArray(r.data?.data)) return;
      const active = r.data.data.filter((t) => t.status === 'active');
      setTenants(active);
      setTenantCode((prev) => {
        if (prev && active.some((t) => t.code === prev)) return prev;
        const platform = active.find((t) => t.tenant_type === 'platform');
        return (platform || active[0])?.code || '';
      });
    }).catch(() => {});
  }, [isOperator]);

  // ── 운영자: 선택 테넌트의 회사 목록 ──────────────────────────────────────
  //   ledgerTenant = 지금 들고 있는 회사 목록이 **어느 테넌트 것인가**.
  //   테넌트를 바꾸면 목록이 도착하기 전까지는 옛 테넌트의 회사가 남아 있어서,
  //   이 표시가 없으면 화면 이동이 옛 회사로 판단해 버린다.
  useEffect(() => {
    if (!isOperator || !tenantCode) return;
    apiClient.get(`/api/admin/saas/tenants/${tenantCode}/companies`).then((r) => {
      if (!r.ok || !Array.isArray(r.data?.data)) { setLedgerCompanies([]); setLedgerTenant(tenantCode); return; }
      setLedgerCompanies(r.data.data.filter((c) => c.is_active !== false && c.external_active !== false));
      setLedgerTenant(tenantCode);
    }).catch(() => { setLedgerCompanies([]); setLedgerTenant(tenantCode); });
  }, [isOperator, tenantCode]);

  // ── 선택 가능한 회사 목록 조립 ────────────────────────────────────────────
  const selectedTenant = useMemo(
    () => tenants.find((t) => t.code === tenantCode) || null,
    [tenants, tenantCode],
  );
  const companies = useMemo(() => {
    if (isOperator) {
      const platform = selectedTenant?.tenant_type === 'platform';
      return ledgerCompanies.map((c) => {
        const kind = (c.is_primary && platform) ? 'primary' : 'ext';
        return {
          code: c.code,
          name: c.external_name || c.name || c.code,
          enabled_features: c.enabled_features || {},
          // 2026-10-19 회사 메뉴 규칙: 운영 테넌트의 추가 회사(파인그룹 DOT·LNJ)는 그 회사가 받은 메뉴만(서버 company_menu_features).
          //   못 받았으면 {} = 메뉴 없음(닫힘). 고객 테넌트는 예전처럼(메뉴는 menu-perms 가 정한다).
          accessible_features: (platform && kind === 'ext') ? (c.company_menu_features || {}) : null,
          kind,
        };
      });
    }
    // 비운영자: 자기 회사(본사 메뉴) + 접근 가능한 외부 회사. 자기 회사가 목록에 있으면 own 으로 승격.
    const list = [];
    const ownExt = externalCompanies.find((c) => String(c.code).toUpperCase() === myCompanyCode);
    if (myCompanyCode) {
      list.push({
        code: ownExt?.code || myCompanyCode,
        name: ownExt?.name || me?.company?.name || '내 회사',
        // ⚠ ownExt(외부회사 목록에 자기 회사가 있는 경우 = 테넌트 관리자류)일 때만 메뉴 세트 재료를 넣는다.
        //   일반 직원에게 빈 객체 {} + af null(=관리자 의미)을 주면 급여 관리 등 own 메뉴가 전원 노출됨
        //   (2026-09-07 실사고 — 김정희 급여 관리 노출. null 이면 Sidebar/FeatureLauncher 가드가 차단).
        enabled_features: ownExt ? (ownExt.enabled_features || {}) : null,
        accessible_features: ownExt?.accessible_features ?? null,
        kind: 'own',
      });
    }
    for (const c of externalCompanies) {
      if (String(c.code).toUpperCase() === myCompanyCode) continue;
      list.push({ ...c, kind: 'ext' });
    }
    return list;
  }, [isOperator, ledgerCompanies, selectedTenant, externalCompanies, myCompanyCode, me]);

  // ── 선택 회사 결정 (저장값 → 기본: primary/own → 첫 항목) ──────────────────
  const company = useMemo(() => {
    if (companies.length === 0) return null;
    return companies.find((c) => String(c.code).toUpperCase() === String(companyCode).toUpperCase())
      || companies.find((c) => c.kind === 'primary' || c.kind === 'own')
      || companies[0];
  }, [companies, companyCode]);

  // 서버 선택값은 **회사 목록이 지금 테넌트 것으로 도착한 뒤에만** 정한다(아래 writeScopeCompany).
  const listReady = !isOperator || (tenants.length > 0 && ledgerTenant === tenantCode);

  // ── 운영자가 고른 고객 테넌트 (2026-09-15) ──────────────────────────────────
  //   ① 그 테넌트 관리자와 같은 메뉴를 그리려고 서버에서 권한을 받는다(menu-perms, useCustomerTenantView.js).
  //   ② 서버가 그 회사 데이터만 주도록 요청에 회사를 싣는다(scopeCompany → apiClient 헤더).
  //   아래 통합관리 판단이 이 권한을 쓰므로 그보다 먼저 둔다(2026-10-24).
  const isCustomerTenant = isOperator && selectedTenant?.tenant_type === 'customer';
  const customerView = useCustomerTenantView({ isCustomerTenant, selectedTenant, company });

  // ── 통합관리 (2026-10-01) — 운영자는 고른 테넌트 회사 안의 메뉴만, 그 외는 내 열쇠 목록 그대로 (useIntegratedAccess.js) ──
  //   메뉴가 0개여도 칸이 보이는 '통합 메뉴 관리' 열쇠: 비운영자 = 내 열쇠 / 운영자가 고른 고객 테넌트 = 그 테넌트 관리자의
  //   같은 열쇠(menu-perms). 2026-10-24 사용자: "통합관리가 pinegroup에는 있는데 testtenant에는 왜 없지? 테넌트를 새로
  //   만들어도 있어야" → 칸만 보이기(a) · 관리자와 똑같이(a). 운영사 자기 테넌트는 종전대로(메뉴가 있을 때).
  //   열쇠가 오기 전에는 판단을 미룬다(manageReady) — 통합 주소 새로고침 때 홈으로 튕기지 않게.
  const integratedManageKey = isOperator
    ? customerView?.permCodes?.has('INTEGRATED_MENU_MANAGEMENT') === true
    : menuManageKey === true;
  //   회사가 하나도 없는 테넌트는 customerView 가 없다(null) — 기다릴 것 없이 열쇠 없음으로 본다.
  const integratedManageReady = !isCustomerTenant || !company || customerView?.ready === true;
  const {
    integratedMenus, canUseIntegrated, integratedReady, integrated, canSeeHome, selectIntegrated,
  } = useIntegratedAccess({
    isOperator, integratedList, listReady, ledgerCompanies, pathname, homeKey, navigate,
    manageKey: integratedManageKey, manageReady: integratedManageReady,
  });
  // 통합 메뉴 관리(만들기·고치기·지우기)를 쓸 수 있나 — 운영자 또는 열쇠. manageKeyReady = 그 답을 아는가(관리 주소 문지기가 기다린다)
  const canManageIntegrated = isOperator || menuManageKey === true;
  const manageKeyReady = isOperator || menuManageKey !== null;

  // ── 딥링크 동기화: /admin/ext/{code}/* 또는 /admin/payout-management-{code} 진입 시 그 회사 선택 ──
  useEffect(() => {
    const m = pathname.match(/^\/admin\/ext\/([^/]+)/) || pathname.match(/^\/admin\/payout-management-([A-Za-z0-9_]+)/);
    if (!m) return;
    const code = m[1].toUpperCase();
    if (companies.some((c) => String(c.code).toUpperCase() === code)) {
      setCompanyCode((prev) => (String(prev).toUpperCase() === code ? prev : code));
    }
  }, [pathname, companies]);

  // 테넌트를 바꾸면 회사가 곧바로 정해지지 않는다 — 그 테넌트의 회사 목록을 서버에서 받아온 뒤에야
  // 정해진다. 그래서 "화면도 옮겨 달라" 는 표시만 남기고, 회사가 정해지면 아래 effect 가 옮긴다.
  //   ⚠ 미팅링크는 테넌트마다 회사가 하나뿐이라 **회사 칸이 비활성**이다. 유기자차로 가는 길은
  //     테넌트 칸뿐이므로, 이 경로를 처리하지 않으면 화면이 안 따라간다(2026-09-14 신고).
  const switchRequested = useRef(false);

  // useCallback — 통합 모드 값을 읽으므로 그 값이 바뀌면 새로 만든다(옛 값으로 판단하지 않게).
  // opts { company, to } — PC 즐겨찾기(2026-10-22): 그 회사로 정하고, 회사가 정해지면 정해 둔 화면으로 간다.
  const selectTenant = useCallback((code, opts = {}) => {
    setTenantCode(code);
    setCompanyCode(opts.company || ''); // 테넌트 바꾸면 회사는 기본(primary/첫 항목)으로
    switchRequested.current = opts.to ? { to: opts.to } : (integrated ? 'integrated' : true); // 통합 모드에서 바꾸면 통합 화면을 빠져나온다
    writeScopeCompany(null); // 새 테넌트의 회사가 정해질 때까지 옛 회사로 좁히지 않는다
    try {
      localStorage.setItem(LS_TENANT, code);
      if (opts.company) localStorage.setItem(LS_COMPANY, opts.company); else localStorage.removeItem(LS_COMPANY);
    } catch (_) { /* ignore */ }
  }, [integrated]);
  // 회사와 무관해서 작업대상을 바꿔도 그대로 둘 화면들 (로그·DB·테넌트 관리 등)
  const stayPaths = useMemo(
    () => buildStayPaths(MENU_GROUPS, GLOBAL_GROUP_NAMES, ADMIN_ONLY_MENUS),
    [],
  );

  // ── 본사(이 사이트의 주인 회사) 코드 ─────────────────────────────────────
  //   정본은 서버(/api/me 의 hqCompanyCode). 판정 규칙은 companyFeatures.js 에 한 벌로 둔다.
  const hqCompanyCode = useMemo(
    () => resolveHqCompanyCode(me, externalCompanies),
    [me, externalCompanies],
  );

  const guard = useSwitchGuard(companies, isOperator, canSeeHome, boardKey); // 열쇠 없는 회사 메뉴로는 안 넘어간다(2026-11-05)
  // 통합관리에서 회사를 골랐을 때 갈 곳 — 홈 열쇠가 없으면 들어오기 전 회사 화면(그 회사에 맞춰) → 상황판 → 첫 주소(2026-10-29 나)
  const leaveIntegrated = useCallback((to, nextCode, nextIsHq) => {
    const backTo = backToCompanyScreen({ lastPath: lastCompanyScreen(), nextCode, nextIsHq, stayPaths, guard });
    const dest = exitIntegratedTarget(to, { canSeeHome, canSeeBoard: isOperator || boardKey, backTo });
    if (dest === '/') skipIntegratedRestoreOnce(); // 첫 주소가 방금 보던 통합 메뉴를 되살려 되돌아가지 않게
    return dest;
  }, [stayPaths, canSeeHome, isOperator, boardKey, guard]);

  // useCallback 이어야 한다 — 이 함수가 pathname 을 붙잡고 있어서, 한 번 만들어 둔 채로
  // 재사용하면 **화면을 옮긴 뒤에도 옛 주소로 판단**한다.
  // opts.to — PC 즐겨찾기(2026-10-22): 화면 옮길 곳을 정해 두고 회사만 바꾼다.
  const selectCompany = useCallback((code, opts = {}) => {
    if (code === INTEGRATED_OPTION) return; // 통합관리는 회사가 아니다 — selectIntegrated
    setCompanyCode(code);
    try { localStorage.setItem(LS_COMPANY, code); } catch (_) { /* ignore */ }
    // 화면 이동 전에 서버 선택값부터 바꾼다 — 옮겨 간 화면의 첫 요청이 새 회사로 나가게.
    writeScopeCompany(isOperator && selectedTenant?.tenant_type === 'customer' ? code : null);

    // 화면도 같이 옮긴다 (2026-09-14). 전에는 선택창만 바뀌고 화면이 남아
    // "미팅링크 매입 관리를 보는데 작업대상은 유기자차" 같은 상태가 됐다.
    const picked = companies.find((c) => String(c.code).toUpperCase() === String(code).toUpperCase());
    const nextIsHq = Boolean(hqCompanyCode)
      && String(code).toUpperCase() === String(hqCompanyCode).toUpperCase();
    const to = resolveCompanySwitchTarget({
      pathname,
      nextCode: picked?.code || code,   // 주소는 원본 케이스를 유지한다
      nextIsHq,
      stayPaths, guard,
    });
    // 통합 모드에서 회사를 고르면 통합 화면을 빠져나온다(통합 메뉴 관리는 머무는 화면이라 그대로 두면 못 나온다)
    const dest = opts.to || (integrated ? leaveIntegrated(to, picked?.code || code, nextIsHq) : to);
    if (dest) navigate(dest);
  }, [companies, hqCompanyCode, pathname, stayPaths, navigate, isOperator, selectedTenant, integrated, leaveIntegrated, guard]);

  // 테넌트 전환으로 회사가 새로 정해졌을 때 화면을 옮긴다.
  //   표시(switchRequested)가 있을 때만 움직인다 — 새로고침이나 딥링크로 회사가 정해지는 것은
  //   사용자가 "옮겨 달라" 고 한 게 아니므로 보던 화면을 뺏지 않는다.
  useEffect(() => {
    if (!switchRequested.current) return;
    // 운영자는 회사 목록이 **새 테넌트 것으로** 바뀐 뒤에 판단해야 한다.
    if (isOperator && ledgerTenant !== tenantCode) return;
    if (!company?.code) return; // 아직 회사가 안 정해졌다
    const request = switchRequested.current;
    const fromIntegrated = request === 'integrated';
    switchRequested.current = false;
    if (request?.to) { navigate(request.to); return; } // 즐겨찾기 — 정해 둔 화면으로
    const nextIsHq = Boolean(hqCompanyCode)
      && String(company.code).toUpperCase() === String(hqCompanyCode).toUpperCase();
    const to = resolveCompanySwitchTarget({
      pathname, nextCode: company.code, nextIsHq, stayPaths, guard,
    });
    const dest = fromIntegrated ? leaveIntegrated(to, company.code, nextIsHq) : to;
    if (dest) navigate(dest);
  }, [company, hqCompanyCode, pathname, stayPaths, navigate, isOperator, ledgerTenant, tenantCode, leaveIntegrated, guard]);

  // 서버 선택값은 **회사 목록이 지금 테넌트 것으로 도착한 뒤에만** 정한다(listReady). 그 전엔 저장값을 그대로 둔다 —
  //   새로고침 직후 자식 화면의 첫 요청이 이 effect 들보다 먼저 나가므로, 렌더 중에 곧바로 맞춘다.
  //   통합 주소는 회사 하나로 좁히지 않는다(통합 화면은 메뉴 열쇠로 범위를 정한다) — 목록 도착을 기다리지 않고 바로.
  //   (기다리면 새로고침 직후 회사 목록이 통합 메뉴 목록보다 먼저 온 사이의 요청에 옛 회사가 붙었다 — 2026-10-01 실측)
  if (isIntegratedPath(pathname)) writeScopeCompany(null);
  else if (listReady) writeScopeCompany(isCustomerTenant && company ? company.code : null);

  // 선택창 노출: 운영자는 항상, 비운영자는 접근 가능한 외부 회사나 통합 메뉴가 있을 때만 (일반 직원 = 비노출)
  const showSelector = isOperator || externalCompanies.length > 0 || canUseIntegrated;

  // me 가 도착해야 주인 회사를 알 수 있다. 그 전에 화면을 그리면 'PINE' 으로 한 번 물었다가
  // 다시 묻게 되어 빈 화면이 깜빡인다. 본사 화면들은 이 값이 true 가 될 때까지 기다린다.
  const hqReady = Boolean(me);

  // 일일콜 관리 메뉴 이름(통합/회사 하나) 판단용 내 테넌트 요약 (2026-09-24, callMenuNaming.js)
  const tenantInfo = useTenantMenuInfo();

  // A '(통합)' 칸: 자기 테넌트 일일콜 회사가 정확히 1곳일 때만(2026-10-14, callMenuNaming.js isCallAllVisible).
  //   canOpenCallHq — 옛 A 주소로 왔을 때 본사 화면(B)으로 보낼 수 있나. 고객 테넌트를 고른 운영자는 B 가 막혀 있어 제외.
  const callAllVisible = isCallAllVisible(me);
  const canOpenCallHq = !isCustomerTenant && (isOperator || callHqKey);

  const value = useMemo(() => ({
    isOperator, showSelector, hqCompanyCode, hqReady,
    tenants, selectedTenant, selectTenant,
    companies, company, selectCompany, customerView, tenantInfo,
    // companiesReady: companies 가 지금 고른 테넌트 것으로 도착했나(바꾸는 사이엔 옛 테넌트 회사가 남아 있다, 2026-10-07)
    companiesReady: listReady,
    integrated, canUseIntegrated, integratedMenus, integratedReady, canSeeHome, selectIntegrated,
    refreshIntegratedMenus: integratedList.refresh,
    callAllVisible, canOpenCallHq, canManageIntegrated, manageKeyReady,
  }), [isOperator, showSelector, hqCompanyCode, hqReady, tenants, selectedTenant, selectTenant, companies, company, selectCompany,
    customerView, tenantInfo, listReady, integrated, canUseIntegrated, integratedMenus, integratedReady, canSeeHome,
    selectIntegrated, integratedList.refresh, callAllVisible, canOpenCallHq, canManageIntegrated, manageKeyReady]);

  return <CompanyScopeContext.Provider value={value}>{children}</CompanyScopeContext.Provider>;
}
